import React, { useContext, useState, useEffect } from "react";
import { useTranslate } from "react-admin";
import { 
    Button, Dialog, DialogTitle, DialogContent, 
    DialogActions, TextField as MuiTextField, CircularProgress 
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { httpClient } from 'api/httpClient'; 
import { CopyUserSettingsContext } from "contexts/CopyUserSettingsContext";

export const CopySettingsDialog = () => {
    const { 
        isModalOpen, setIsModalOpen, 
        setSourceUserId, tempSourceId, 
        setSelectedKeys 
    } = useContext(CopyUserSettingsContext);
    
    const translate = useTranslate();
    
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const [localSelection, setLocalSelection] = useState([]);

    useEffect(() => {
        if (isModalOpen) {
            setLoading(true);
            setLocalSelection([]); 

            httpClient(`/user-data-keys/allowed-to-copy`, { method: "GET" })
            .then(({ json }) => {
                const keys = json.keys || (Array.isArray(json) ? json : []);
                
                const optionsWithLabels = keys.map(key => {
                    let label;
                    // handle translation for columns order, because it depends on resource
                    if (key.startsWith('columns-order-')) {
                        // Translate the base "columns-order"
                        const baseTranslation = translate('osfc.user_data.key.columns-order');
                        
                        //  Get the rest of the string and clean -
                        const suffix = key.replace('columns-order-', '').replace(/-/g, ' ');
                        
                        label = `${baseTranslation} ${suffix}`;
                    } else {
                        // for other keys
                        label = translate(`osfc.user_data.key.${key}`);
                    }

                    return {
                        key: key,
                        label: label
                    };
                });
                
                setOptions(optionsWithLabels);
            })
            .catch((error) => {
                setOptions([]);
            })
            .finally(() => setLoading(false));
        }
    }, [isModalOpen, translate]);

    const handleConfirm = () => {
        setSourceUserId(tempSourceId);
        const selectedKeysOnly = localSelection.map(item => item.key);
        setSelectedKeys(selectedKeysOnly);
        
        setLocalSelection([]); 
        setIsModalOpen(false);
    };

    const handleClose = () => {
        setIsModalOpen(false);
        setLocalSelection([]);
    };

    const handleSelectAll = () => {
        setLocalSelection(options);
    };

    return (
        <Dialog open={isModalOpen} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Select Settings to Copy</DialogTitle>
            <DialogContent>
                {loading ? (
                    <CircularProgress size={20} style={{ display: 'block', margin: '20px auto' }} />
                ) : (
                    <Autocomplete
                        multiple
                        options={options}
                        getOptionLabel={(option) => option.label}
                        getOptionSelected={(option, value) => option.key === value.key}
                        value={localSelection}
                        onChange={(event, newValue) => setLocalSelection(newValue)}
                        renderInput={(params) => (
                            <MuiTextField 
                                {...params} 
                                variant="outlined" 
                                label={translate('osfc.fields.fields_to_copy')} 
                                placeholder="Select fields..." 
                            />
                        )}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={handleSelectAll} 
                    color="primary" 
                    disabled={options.length === 0}
                    style={{ marginRight: 'auto' }}
                >
                    {translate('osfc.buttons.select_all')}
                </Button>
                <Button onClick={handleClose} color="primary">{translate('osfc.buttons.copy.title')}</Button>
                <Button 
                    onClick={handleConfirm} 
                    color="primary" 
                    variant="contained" 
                    disabled={localSelection.length === 0}
                >
                    {translate('osfc.buttons.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};